import { useEffect, useState } from 'react';
import { Plus, Trash2, Pencil, X, AlertCircle } from 'lucide-react';
import { usersAPI, type LegacyUser } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useModalKeys } from '../hooks/useModalKeys';
import {
  ASSIGNABLE_ROLES,
  ROLE_LABEL,
  userRoles,
  type SaasRole,
} from '../utils/roles';

const ROLE_HINT: Record<string, string> = {
  admin: 'acesso total',
  monitor: 'dashboard próprio + módulo Agendamento',
  financeiro: 'módulos futuros',
  vendedor: 'prospect no nível vendedor',
  gestao_vendas: 'acesso total ao módulo Prospect',
  gestao_projetos: 'módulos futuros',
};

interface FormState {
  id?: string;
  name: string;
  email: string;
  password: string;
  roles: SaasRole[];
  isActive: boolean;
}

const EMPTY: FormState = {
  name: '',
  email: '',
  password: '',
  roles: [],
  isActive: true,
};

export default function SaasUsers() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<LegacyUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [saving, setSaving] = useState(false);

  const isAdmin = currentUser?.role === 'admin';

  useEffect(() => {
    load();
  }, []);

  async function load() {
    setLoading(true);
    try {
      const r = await usersAPI.getUsers();
      setUsers(Array.isArray(r) ? (r as LegacyUser[]) : []);
      setError(null);
    } catch (err) {
      const e = err as { response?: { data?: { message?: string } } };
      setError(e.response?.data?.message ?? 'Falha ao carregar usuários');
    } finally {
      setLoading(false);
    }
  }

  function openCreate() {
    setForm(EMPTY);
    setShowForm(true);
  }

  function openEdit(u: LegacyUser) {
    setForm({
      id: u.id,
      name: u.name,
      email: u.email,
      password: '',
      roles: userRoles(u),
      isActive: (u as { isActive?: boolean }).isActive ?? true,
    });
    setShowForm(true);
  }

  function toggleRole(r: SaasRole) {
    setForm((f) => ({
      ...f,
      roles: f.roles.includes(r) ? f.roles.filter((x) => x !== r) : [...f.roles, r],
    }));
  }

  async function handleSave() {
    if (!form.name || !form.email) {
      setError('Nome e email são obrigatórios');
      return;
    }
    if (!form.id && form.password.length < 6) {
      setError('Senha precisa ter ao menos 6 caracteres');
      return;
    }
    if (form.roles.length === 0) {
      setError('Selecione ao menos um perfil de acesso');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (form.id) {
        const payload: Partial<LegacyUser> & { password?: string; roles?: SaasRole[] } = {
          name: form.name,
          email: form.email,
          roles: form.roles,
          isActive: form.isActive,
        };
        if (form.password) payload.password = form.password;
        await usersAPI.updateUser(form.id, payload);
      } else {
        await usersAPI.createUser({
          name: form.name,
          email: form.email,
          password: form.password,
          roles: form.roles,
          isActive: form.isActive,
        } as Parameters<typeof usersAPI.createUser>[0]);
      }
      setShowForm(false);
      await load();
    } catch (err) {
      const e = err as { response?: { data?: { message?: string } } };
      setError(e.response?.data?.message ?? 'Falha ao salvar');
    } finally {
      setSaving(false);
    }
  }

  // Modal do formulário: Esc fecha, Enter salva (Fase 30)
  useModalKeys(
    () => setShowForm(false),
    () => {
      if (!saving) void handleSave();
    },
    showForm,
  );

  async function handleDelete(u: LegacyUser) {
    if (u.id === currentUser?.id) {
      alert('Você não pode excluir seu próprio usuário.');
      return;
    }
    if (!confirm(`Excluir ${u.email}?`)) return;
    try {
      await usersAPI.deleteUser(u.id);
      await load();
    } catch (err) {
      const e = err as { response?: { data?: { message?: string } } };
      alert(e.response?.data?.message ?? 'Falha ao excluir');
    }
  }

  return (
    <div className="p-6 md:p-10 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-fg">Usuários SAAS</h1>
          <p className="text-sm text-fg-2 mt-1">
            Usuários do painel — um usuário pode acumular vários perfis de acesso.
          </p>
        </div>
        {isAdmin && (
          <button
            onClick={openCreate}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-solid hover:bg-primary-solid-hover text-white font-medium"
          >
            <Plus size={18} />
            Novo usuário
          </button>
        )}
      </div>

      {error && (
        <div className="flex items-start gap-2 px-4 py-3 rounded-lg bg-danger-soft/30 border border-danger-line/50 text-sm text-danger mb-4">
          <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <div className="bg-surface border border-line rounded-2xl overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-muted">Carregando…</div>
        ) : users.length === 0 ? (
          <div className="p-8 text-center text-muted">Nenhum usuário cadastrado.</div>
        ) : (
          <table className="w-full">
            <thead className="bg-canvas border-b border-line">
              <tr className="text-left text-xs uppercase tracking-wider text-muted">
                <th className="px-4 py-3">Nome</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Perfis</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {users.map((u) => (
                <tr key={u.id} className="hover:bg-canvas/50">
                  <td className="px-4 py-3 text-fg">{u.name}</td>
                  <td className="px-4 py-3 text-fg-2">{u.email}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {userRoles(u).map((r) => (
                        <span
                          key={r}
                          className="inline-block px-2 py-0.5 rounded-full bg-primary-soft/50 border border-primary-line/50 text-xs text-primary"
                        >
                          {ROLE_LABEL[r] ?? r}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {(u as { isActive?: boolean }).isActive === false ? (
                      <span className="text-xs text-danger">Inativo</span>
                    ) : (
                      <span className="text-xs text-success">Ativo</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {isAdmin && (
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => openEdit(u)}
                          className="p-2 rounded-lg text-fg-2 hover:bg-hover hover:text-fg"
                          aria-label="Editar"
                        >
                          <Pencil size={16} />
                        </button>
                        <button
                          onClick={() => handleDelete(u)}
                          className="p-2 rounded-lg text-fg-2 hover:bg-danger-soft/30 hover:text-danger"
                          aria-label="Excluir"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="bg-surface border border-line rounded-2xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-fg">
                {form.id ? 'Editar usuário' : 'Novo usuário'}
              </h2>
              <button
                onClick={() => setShowForm(false)}
                className="text-muted hover:text-fg-2"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-sm text-fg-2 mb-1">Nome</label>
                <input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg bg-canvas border border-active text-fg focus:border-primary focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm text-fg-2 mb-1">Email</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg bg-canvas border border-active text-fg focus:border-primary focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm text-fg-2 mb-1">
                  Senha {form.id && <span className="text-muted text-xs">(deixe em branco pra manter)</span>}
                </label>
                <input
                  type="password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg bg-canvas border border-active text-fg focus:border-primary focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm text-fg-2 mb-1">
                  Perfis de acesso{' '}
                  <span className="text-muted text-xs">(pode marcar mais de um)</span>
                </label>
                <div className="space-y-1.5 rounded-lg bg-canvas border border-active p-3">
                  {ASSIGNABLE_ROLES.map((r) => (
                    <label
                      key={r}
                      className="flex items-center gap-2 text-sm text-fg cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={form.roles.includes(r)}
                        onChange={() => toggleRole(r)}
                        className="w-4 h-4 rounded bg-surface border-active accent-blue-600"
                      />
                      <span className="font-medium">{ROLE_LABEL[r]}</span>
                      <span className="text-xs text-muted">— {ROLE_HINT[r]}</span>
                    </label>
                  ))}
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm text-fg-2">
                <input
                  type="checkbox"
                  checked={form.isActive}
                  onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                  className="w-4 h-4 rounded bg-canvas border-active"
                />
                Ativo
              </label>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => setShowForm(false)}
                className="px-4 py-2 rounded-lg text-fg-2 hover:bg-hover"
              >
                Cancelar
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 rounded-lg bg-primary-solid hover:bg-primary-solid-hover disabled:bg-primary-soft text-white font-medium"
              >
                {saving ? 'Salvando…' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
